import { trim } from 'ramda'

const OAUTH_REQUIRED_PARAMS = Object.freeze([
  'response_type',
  'client_id',
  'redirect_uri',
  'code_challenge',
  'code_challenge_method',
  'resource',
])
const OAUTH_OPTIONAL_PARAMS = Object.freeze(['scope', 'state'])
const OAUTH_PARAM_MAX_LENGTH = 2048

/**
 * Normalize one OAuth query parameter from the browser URL.
 *
 * OAuth params arrive from an external client via the address bar, so the FE accepts only scalar strings, trims
 * incidental whitespace, and rejects empty or unexpectedly large values before any API request is made.
 */
const sanitizeOAuthString = value => {
  if (typeof value !== 'string') return null
  const trimmed = trim(value)
  if (!trimmed || trimmed.length > OAUTH_PARAM_MAX_LENGTH) return null
  return trimmed
}

/**
 * Return whether a query parameter is an HTTP(S) URL.
 *
 * The backend performs authoritative OAuth validation. The FE still rejects obviously unsafe URL shapes so cancel
 * redirects and context lookups only use the same URL class the backend is willing to validate.
 */
const isHttpUrl = value => {
  try {
    const url = new URL(value)
    return url.protocol === 'https:' || url.protocol === 'http:'
  } catch {
    return false
  }
}

/**
 * Validate and sanitize the OAuth authorization request params used by AROYA FE.
 *
 * Valid shape mirrors `api.views.oauth._authorize_query_string` in aroya-be: required `response_type`, `client_id`,
 * `redirect_uri`, `code_challenge`, `code_challenge_method`, and `resource`; optional `scope` and `state`. Unknown
 * params are dropped, repeated/non-string params are rejected, and the result is safe to send to
 * `/oauth/authorize/context/` and `/oauth/authorize/approve/`.
 */
export const sanitizeOAuthAuthorizeParams = queryObject => {
  const sanitized = OAUTH_REQUIRED_PARAMS.reduce((params, key) => {
    const value = sanitizeOAuthString(queryObject[key])
    if (!value) return null
    return { ...params, [key]: value }
  }, {})
  if (!sanitized) return null

  const withOptional = OAUTH_OPTIONAL_PARAMS.reduce((params, key) => {
    const value = sanitizeOAuthString(queryObject[key])
    if (!value) return params
    return { ...params, [key]: value }
  }, sanitized)

  if (withOptional.response_type !== 'code') return null
  if (withOptional.code_challenge_method !== 'S256') return null
  if (!isHttpUrl(withOptional.redirect_uri)) return null
  if (!isHttpUrl(withOptional.resource)) return null

  return withOptional
}
